import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Tooltip, Paper, DialogContent } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {
  Clock,
  DoorOpen,
  Users,
  ExternalLink,
  RefreshCw,
  LoaderCircle,
  CheckCircle2,
  XCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { CalendarEvent } from "@/api/calendarTypes";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/Button/Button";
import { NewMeeting, NewMeetingRef } from "@/components/Calendar/new-meeting";
import { MsalDisabledOverlay } from "@/components/MsalDisabledOverlay";
import { Skeleton } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { getResourceCalendarEvents } from "@/services/graphServices/getResourceCalendarEvents";

dayjs.extend(isBetween);

// Category-based color mapping (matching Activities and MeetingRooms)
const getCategoryColor = (category?: string) => {
  const cat = category?.toLowerCase();

  if (cat?.includes("phone")) {
    return { bg: "bg-green-500/60", hover: "hover:bg-green-600" };
  }
  if (cat?.includes("meeting")) {
    return { bg: "bg-blue-500/60", hover: "hover:bg-blue-600" };
  }
  if (cat?.includes("task")) {
    return { bg: "bg-orange-500/60", hover: "hover:bg-orange-600" };
  }
  if (cat?.includes("private")) {
    return { bg: "bg-purple-500/60", hover: "hover:bg-purple-600" };
  }
  if (cat?.includes("compliance")) {
    return { bg: "bg-red-500/60", hover: "hover:bg-red-600" };
  }

  // Default blue for uncategorized (same as Meeting)
  return { bg: "bg-blue-500/60", hover: "hover:bg-blue-600" };
};

const MEETING_ROOMS = [
  { email: "moterom1@capguard.no", name: "Møterom 1", capacity: 12 },
  { email: "moterom2@capguard.no", name: "Møterom 2", capacity: 6 },
  { email: "moterom3@capguard.no", name: "Møterom 3", capacity: 6 },
  { email: "moterom4@capguard.no", name: "Møterom 4", capacity: 4 },
];

const TIME_SLOTS = [
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
];

interface TimeSlot {
  start: Dayjs;
  end: Dayjs;
  label: string;
}

interface RoomData {
  room: (typeof MEETING_ROOMS)[0];
  events: CalendarEvent[];
  isCurrentlyAvailable: boolean;
  currentEvent: CalendarEvent | null;
  nextAvailable: TimeSlot | null;
  availabilityPercentage: number;
  availableSlots: TimeSlot[];
  bookedSlots: TimeSlot[];
}

interface MeetingRoomAvailabilityV3Props {
  onNewMeeting?: () => void;
  asButton?: boolean;
}

const MeetingRoomAvailabilityV3 = ({
  onNewMeeting,
  asButton = false,
}: MeetingRoomAvailabilityV3Props) => {
  const { getToken, isMsalDisabled } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [roomEvents, setRoomEvents] = useState<Record<string, CalendarEvent[]>>(
    {}
  );
  const [roomErrors, setRoomErrors] = useState<Record<string, boolean>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const meetingRef = useRef<NewMeetingRef>(null);
  const navigate = useNavigate();

  const disabled = isMsalDisabled;

  const fetchRoomAvailability = useCallback(
    async (skipCache = false) => {
      if (disabled) {
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          // Mark all rooms as errored so they don't appear as available
          // (initialLoading will still become false via finally)
          const noTokenErrors: Record<string, boolean> = {};
          MEETING_ROOMS.forEach((r) => {
            noTokenErrors[r.email] = true;
          });
          setRoomErrors(noTokenErrors);
          return;
        }

        setLoading(true);
        const startDate = selectedDate.startOf("day").toISOString();
        const endDate = selectedDate.endOf("day").toISOString();

        const allEvents: Record<string, CalendarEvent[]> = {};
        const errors: Record<string, boolean> = {};

        await Promise.all(
          MEETING_ROOMS.map(async (room) => {
            try {
              const events = await getResourceCalendarEvents(
                token,
                room.email,
                startDate,
                endDate,
                skipCache
              );
              allEvents[room.email] = events || [];
              errors[room.email] = false;
            } catch (error) {
              console.error(`Error fetching events for ${room.name}:`, error);
              allEvents[room.email] = [];
              errors[room.email] = true;
            }
          })
        );

        setRoomEvents(allEvents);
        setRoomErrors(errors);
      } catch (error) {
        console.error("Error fetching room availability:", error);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [getToken, selectedDate, disabled]
  );

  useEffect(() => {
    if (disabled || !dialogOpen) {
      return;
    }

    setInitialLoading(true);
    fetchRoomAvailability();
    const interval = setInterval(fetchRoomAvailability, 20000);
    return () => clearInterval(interval);
  }, [fetchRoomAvailability, disabled, dialogOpen]);

  const getRoomAvailability = useCallback(
    (room: (typeof MEETING_ROOMS)[0]): RoomData => {
      const events = roomEvents[room.email] || [];
      const now = dayjs();
      const availableSlots: TimeSlot[] = [];
      const bookedSlots: TimeSlot[] = [];

      TIME_SLOTS.forEach((time) => {
        const slotStart = selectedDate
          .hour(parseInt(time.split(":")[0]))
          .minute(parseInt(time.split(":")[1]))
          .second(0);
        const slotEnd = slotStart.add(30, "minute");

        const isBooked = events.some((event) => {
          const eventStart = dayjs(event.start?.dateTime);
          const eventEnd = dayjs(event.end?.dateTime);
          const overlaps =
            eventStart.isBefore(slotEnd) && eventEnd.isAfter(slotStart);
          const justTouching =
            eventStart.isSame(slotEnd, "minute") ||
            eventEnd.isSame(slotStart, "minute");
          return overlaps && !justTouching;
        });

        const slot: TimeSlot = {
          start: slotStart,
          end: slotEnd,
          label: time,
        };

        if (isBooked) {
          bookedSlots.push(slot);
        } else {
          availableSlots.push(slot);
        }
      });

      const isToday = selectedDate.isSame(now, "day");
      const isCurrentlyAvailable = isToday
        ? !events.some((event) => {
            const eventStart = dayjs(event.start?.dateTime);
            const eventEnd = dayjs(event.end?.dateTime);
            return now.isBetween(eventStart, eventEnd, null, "[)");
          })
        : availableSlots.length > 0;

      const currentEvent = isToday
        ? events.find((event) => {
            const eventStart = dayjs(event.start?.dateTime);
            const eventEnd = dayjs(event.end?.dateTime);
            return now.isBetween(eventStart, eventEnd, null, "[)");
          }) || null
        : null;

      const nextAvailable = availableSlots.find((slot) =>
        isToday ? slot.start.isAfter(now) : true
      );

      const availabilityPercentage = Math.round(
        (availableSlots.length / TIME_SLOTS.length) * 100
      );

      return {
        room,
        events,
        isCurrentlyAvailable,
        currentEvent,
        nextAvailable: nextAvailable || null,
        availabilityPercentage,
        availableSlots,
        bookedSlots,
      };
    },
    [roomEvents, selectedDate]
  );

  const allRoomData = useMemo(
    () => MEETING_ROOMS.map(getRoomAvailability),
    [getRoomAvailability]
  );

  const availableNow = useMemo(
    () =>
      allRoomData.filter(
        (r) => r.isCurrentlyAvailable && !roomErrors[r.room.email]
      ),
    [allRoomData, roomErrors]
  );

  const handleRoomClick = (roomData: RoomData) => {
    if (roomData.nextAvailable) {
      meetingRef?.current?.open(
        roomData.nextAvailable.start,
        roomData.room.email
      );
    }
  };

  const handleNewMeetingSuccess = () => {
    if (onNewMeeting) {
      onNewMeeting();
    }
    if (asButton) {
      setDialogOpen(false);
    }
  };

  const isToday = selectedDate.isSame(dayjs(), "day");

  const content = (
    <div className="space-y-4">
      <NewMeeting onSuccess={handleNewMeetingSuccess} ref={meetingRef} />

      {/* Two Column Layout: Calendar + Rooms */}
      <div className="flex gap-4 h-[520px]">
        {/* Left: Calendar */}
        <div className="w-fit flex flex-col overflow-hidden">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper sx={{ p: 1, overflow: "hidden" }}>
              <StaticDatePicker
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue);
                  }
                }}
                displayWeekNumber
                disabled={disabled}
                slotProps={{
                  actionBar: {
                    actions: [],
                  },
                }}
                sx={{
                  "& .MuiPickersSlideTransition-root": {
                    overflowY: "hidden",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    paddingY: 0.5,
                  },
                  "& .MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiIconButton-root": {
                    padding: "4px",
                  },
                  "& .MuiDayCalendar-header span": {
                    fontSize: "0.75rem",
                  },
                  "& .MuiDayCalendar-weekContainer": {
                    marginTop: "4px",
                  },
                  "& .MuiButtonBase-root": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Paper>
          </LocalizationProvider>
        </div>

        {/* Right: Grid Layout */}
        <div className="flex-1 overflow-auto pr-2">
          {/* Quick Stats Bar */}
          <div className="flex items-center gap-3 mb-3 pb-2 border-b">
            <div className="text-xs font-semibold text-muted-foreground">
              {isToday ? "Right now:" : `On ${selectedDate.format("MMM D")}:`}
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-50 dark:bg-green-950/20">
              <CheckCircle2 className="size-3 text-green-700 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-700 dark:text-green-400">
                {availableNow.length} {isToday ? "Available now" : "Available"}
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 dark:bg-red-950/20">
              <XCircle className="size-3 text-red-700 dark:text-red-400" />
              <span className="text-xs font-semibold text-red-700 dark:text-red-400">
                {MEETING_ROOMS.length - availableNow.length}{" "}
                {isToday ? "Busy now" : "Booked"}
              </span>
            </div>
            <div className="flex justify-end items-center flex-1">
              <div className="flex items-center gap-1">
                <Button
                  variant="outlinedIcon"
                  size="sm"
                  onClick={() =>
                    setSelectedDate((prev) => prev.subtract(1, "day"))
                  }
                >
                  <ChevronLeft className="size-3.5" />
                </Button>

                <Button
                  variant="outlinedIcon"
                  size="sm"
                  onClick={() => setSelectedDate((prev) => prev.add(1, "day"))}
                >
                  <ChevronRight className="size-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {disabled && (
            <MsalDisabledOverlay
              size="sm"
              message="Enable Microsoft login to view meeting rooms"
            />
          )}

          {initialLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              {MEETING_ROOMS.map((room) => (
                <Skeleton key={room.email} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className={cn(disabled && "blur-sm pointer-events-none")}>
              {/* Time Header */}
              <div className="flex sticky top-0 bg-background z-10 pb-2 border-b">
                {/* Room name column */}
                <div className="w-32 flex-shrink-0" />

                {/* Time slots */}
                <div className="flex-1 flex relative">
                  {TIME_SLOTS.map((time, idx) => (
                    <div
                      key={idx}
                      className="flex-1 text-center text-[10px] font-medium text-muted-foreground px-0.5"
                    >
                      {time}
                    </div>
                  ))}
                </div>
              </div>

              {/* Room Rows */}
              <div className="relative space-y-1.5 pt-2">
                {/* Current-time indicator — only shown when today is selected */}
                {isToday &&
                  (() => {
                    const now = dayjs();
                    const dayStart = selectedDate.hour(8).minute(0).second(0);
                    const dayEnd = selectedDate.hour(17).minute(0).second(0);
                    const totalMinutes = dayEnd.diff(dayStart, "minute");
                    const elapsedMinutes = Math.max(
                      0,
                      Math.min(now.diff(dayStart, "minute"), totalMinutes)
                    );
                    const pct = (elapsedMinutes / totalMinutes) * 100;
                    if (pct <= 0 || pct >= 100) return null;
                    return (
                      <div
                        className="absolute top-0 bottom-0 z-20 pointer-events-none"
                        style={{
                          left: `calc(8rem + (100% - 8rem) * ${pct} / 100)`,
                        }}
                      >
                        <div className="w-px h-full bg-red-500/70 dark:bg-red-400/70" />
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-red-500 text-white text-[8px] font-bold rounded px-1 py-0.5 whitespace-nowrap leading-none">
                          {now.format("HH:mm")}
                        </div>
                      </div>
                    );
                  })()}
                {allRoomData.map((data) => {
                  const {
                    room,
                    isCurrentlyAvailable,
                    currentEvent,
                    nextAvailable,
                  } = data;

                  const hasError = roomErrors[room.email];

                  return (
                    <div
                      key={room.email}
                      className="flex items-center py-2 hover:bg-muted/30 rounded-lg transition-colors"
                    >
                      {/* Room Info */}
                      <div className="w-32 flex-shrink-0 pr-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="text-xs font-semibold truncate">
                            {room.name}
                          </div>
                          {hasError ? (
                            <div className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                              N/A
                            </div>
                          ) : isCurrentlyAvailable ? (
                            <div className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              FREE
                            </div>
                          ) : (
                            <div className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                              BUSY
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Users className="size-2.5" />
                          {room.capacity}
                        </div>
                        {!isCurrentlyAvailable && currentEvent && (
                          <div className="text-[9px] text-red-600 dark:text-red-400 mt-0.5">
                            Until{" "}
                            {dayjs(currentEvent.end?.dateTime).format("HH:mm")}
                          </div>
                        )}
                        {!isCurrentlyAvailable &&
                          !currentEvent &&
                          nextAvailable && (
                            <div className="text-[9px] text-muted-foreground mt-0.5">
                              Next: {nextAvailable.start.format("HH:mm")}
                            </div>
                          )}
                      </div>

                      {/* Slots */}
                      <div className="flex-1 flex gap-0.5">
                        {hasError ? (
                          <div className="flex-1 h-12 rounded bg-gray-200/60 dark:bg-gray-700/40 flex items-center justify-center gap-1.5 text-[10px] text-gray-400 dark:text-gray-500 italic">
                            <XCircle className="size-3" />
                            Unavailable
                          </div>
                        ) : (
                          TIME_SLOTS.map((timeSlot, idx) => {
                            const slotStart = selectedDate
                              .hour(parseInt(timeSlot.split(":")[0]))
                              .minute(parseInt(timeSlot.split(":")[1]))
                              .second(0);
                            const slotEnd = slotStart.add(30, "minute");

                            // Check if this slot has passed
                            const now = dayjs();
                            const isPast = slotEnd.isBefore(now);

                            // Find the event that overlaps with this slot
                            const slotEvent = data.events.find((event) => {
                              const eventStart = dayjs(event.start?.dateTime);
                              const eventEnd = dayjs(event.end?.dateTime);
                              return (
                                eventStart.isBefore(slotEnd) &&
                                eventEnd.isAfter(slotStart) &&
                                !(
                                  eventStart.isSame(slotEnd, "minute") ||
                                  eventEnd.isSame(slotStart, "minute")
                                )
                              );
                            });

                            const isBooked = !!slotEvent;

                            // Get organizer initials
                            const getInitials = (name: string | undefined) => {
                              if (!name) return "";
                              const parts = name.trim().split(" ");
                              if (parts.length >= 2) {
                                return (
                                  parts[0][0] + parts[parts.length - 1][0]
                                ).toUpperCase();
                              }
                              return name.substring(0, 2).toUpperCase();
                            };

                            const organizerName =
                              slotEvent?.organizer?.emailAddress?.name;
                            const initials = organizerName
                              ? getInitials(organizerName)
                              : "";

                            // Get category-based color for booked slots
                            const categoryColor = slotEvent?.categories?.[0]
                              ? getCategoryColor(slotEvent.categories[0])
                              : {
                                  bg: "bg-red-500/60",
                                  hover: "hover:bg-red-600",
                                };

                            return (
                              <Tooltip
                                key={idx}
                                title={
                                  isPast && isBooked
                                    ? `Time passed - ${organizerName} (${timeSlot}–${slotEnd.format(
                                        "HH:mm"
                                      )})`
                                    : isPast
                                    ? `Time passed (${timeSlot}–${slotEnd.format(
                                        "HH:mm"
                                      )})`
                                    : isBooked
                                    ? `${
                                        organizerName || "Unknown"
                                      } (${timeSlot}–${slotEnd.format(
                                        "HH:mm"
                                      )})`
                                    : `Book ${
                                        room.name
                                      } at ${timeSlot}–${slotEnd.format(
                                        "HH:mm"
                                      )}`
                                }
                              >
                                <span className="flex-1 h-12">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isBooked && !isPast) {
                                        meetingRef?.current?.open(
                                          slotStart,
                                          room.email
                                        );
                                      }
                                    }}
                                    disabled={isBooked || isPast}
                                    className={cn(
                                      "w-full h-full rounded transition-all relative",
                                      {
                                        [`${categoryColor.bg} cursor-not-allowed`]:
                                          isBooked,
                                        "bg-green-500/60 cursor-pointer":
                                          !isBooked,
                                        "hover:bg-green-600":
                                          !isBooked && !isPast,
                                        "opacity-50 cursor-not-allowed": isPast,
                                      }
                                    )}
                                  >
                                    {isBooked && initials && (
                                      <span className="text-[9px] font-bold text-white/90 absolute inset-0 flex items-center justify-center">
                                        {initials}
                                      </span>
                                    )}
                                  </button>
                                </span>
                              </Tooltip>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (asButton) {
    return (
      <>
        <Button
          icon={<DoorOpen className="size-3 mr-1.5" />}
          loading={loading}
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
        >
          Meeting Rooms
        </Button>
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { maxHeight: "92vh" },
          }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-2">
              <DoorOpen className="size-5 text-primary" />
              <h1 className="text-lg font-semibold">Meeting Rooms</h1>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip title="Refresh">
                <span>
                  <Button
                    disabled={loading || disabled}
                    variant="icon"
                    size="sm"
                    onClick={() => fetchRoomAvailability(true)}
                  >
                    {loading ? (
                      <LoaderCircle className="size-4 animate-spin" />
                    ) : (
                      <RefreshCw className="size-4" />
                    )}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Full view">
                <span>
                  <Button
                    disabled={disabled}
                    size="sm"
                    variant="icon"
                    onClick={() => {
                      navigate("/meetingrooms");
                      setDialogOpen(false);
                    }}
                  >
                    <ExternalLink className="size-4" />
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="sm"
                variant="icon"
                onClick={() => setDialogOpen(false)}
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
          <DialogContent className="p-4">{content}</DialogContent>
        </Dialog>
      </>
    );
  }

  return content;
};

export default MeetingRoomAvailabilityV3;
